import { StrictMode, lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import './styles.css';
import { AppRouter } from './app/app-router';
import { RouteScrollManager } from './app/route-scroll-manager';
import { CelebrationProvider } from './celebration';
import { MarketingScreenshotPage } from './features/marketing-screenshots/marketing-screenshot-page';
import { registerServiceWorker } from './register-sw';
import { ConsentGate } from './consent/consent-gate';
import { firebaseApp } from './firebase';
import { RenderProfilerRoot } from './monitoring/render-profiler';

import { DeveloperControlsProvider } from './features/app/developer-menu-context';

const DeveloperMenuRoot = import.meta.env.DEV
  ? lazy(() =>
      import('./features/app/developer-menu-root').then((module) => ({
        default: module.DeveloperMenuRoot,
      })),
    )
  : null;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const isExercisePrototypeRoute =
  import.meta.env.DEV && window.location.pathname === '/app/prototype/exercise';

const isMarketingScreenshotRoute =
  import.meta.env.VITE_FORMA_MARKETING_SCREENSHOTS === 'true' &&
  new URLSearchParams(window.location.search).has('marketingScreenshot');

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <CelebrationProvider>
        <RenderProfilerRoot>
          {isMarketingScreenshotRoute ? (
            <MarketingScreenshotPage />
          ) : (
            <BrowserRouter>
              <RouteScrollManager />
              <DeveloperControlsProvider>
                <AppRouter />
                {DeveloperMenuRoot && !isExercisePrototypeRoute ? (
                  <Suspense fallback={null}>
                    <DeveloperMenuRoot />
                  </Suspense>
                ) : null}
              </DeveloperControlsProvider>
              {!isExercisePrototypeRoute && <ConsentGate app={firebaseApp} />}
            </BrowserRouter>
          )}
        </RenderProfilerRoot>
      </CelebrationProvider>
    </QueryClientProvider>
  </StrictMode>,
);

if (!isMarketingScreenshotRoute && !isExercisePrototypeRoute) {
  registerServiceWorker();
}
